import {
  collection,
  doc,
  writeBatch,
  serverTimestamp,
  onSnapshot,
  query,
  orderBy,
  where,
  updateDoc,
} from "firebase/firestore";
import { db } from "../firebase/config";

/**
 * Reviews are split across two collections on purpose:
 *
 *   reviews/{id}         -> name, rating, feedback, status, createdAt
 *                           (publicly readable — this is what shows
 *                           on the site)
 *   reviewContacts/{id}  -> phone only, SAME doc id as the review
 *                           (readable by signed-in admins only)
 *
 * The phone number is never written into the public "reviews" doc at
 * all, so there's no field to accidentally leak through the public
 * read — it physically isn't in that document. Firestore rules only
 * let signed-in users read "reviewContacts". Suggested rules:
 *
 *   match /reviews/{id} {
 *     allow create: if request.resource.data.keys().hasAll(
 *       ['name','rating','feedback']
 *     ) && request.resource.data.rating is number
 *       && request.resource.data.rating >= 1
 *       && request.resource.data.rating <= 5;
 *     allow read: if true;
 *     allow update: if request.auth != null;
 *     allow delete: if false;
 *   }
 *   match /reviewContacts/{id} {
 *     allow create: if request.resource.data.keys().hasAll(['phone']);
 *     allow read: if request.auth != null;
 *     allow update, delete: if false;
 *   }
 */
export async function createReview({ name, rating, feedback, phone }) {
  const reviewRef = doc(collection(db, "reviews"));
  const contactRef = doc(db, "reviewContacts", reviewRef.id);

  const batch = writeBatch(db);
  batch.set(reviewRef, {
    name: name.trim(),
    rating,
    feedback: feedback.trim(),
    status: "pending",
    createdAt: serverTimestamp(),
  });
  batch.set(contactRef, {
    phone: phone.trim(),
    createdAt: serverTimestamp(),
  });

  await batch.commit();
  return reviewRef.id;
}

/** Public: only published reviews, newest first. No phone data ever touched. */
export function subscribeToPublishedReviews(onData, onErr) {
  const q = query(
    collection(db, "reviews"),
    where("status", "==", "published"),
    // orderBy("createdAt", "desc")
  );
  return onSnapshot(
    q,
    (snap) => onData(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
    onErr
  );
}

/** Admin: every review regardless of status. */
export function subscribeToAllReviews(onData, onErr) {
  const q = query(collection(db, "reviews"), orderBy("createdAt", "desc"));
  return onSnapshot(
    q,
    (snap) => onData(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
    onErr
  );
}

/** Admin only — this is the one place phone numbers are ever read. */
export function subscribeToReviewContacts(onData, onErr) {
  return onSnapshot(
    collection(db, "reviewContacts"),
    (snap) => {
      const map = {};
      snap.docs.forEach((d) => {
        map[d.id] = d.data().phone;
      });
      onData(map);
    },
    onErr
  );
}

export function updateReviewStatus(id, status) {
  return updateDoc(doc(db, "reviews", id), { status });
}
